import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import {
    ITypeListResponse,
    ITenantDefinitionResponse,
    ICreateTenantRequest,
    IUpdateTenantRequest,
    ITenantUserDefinitionResponse,
    ICreateTenantUserRequest,
    IUpdateTenantUserRequest,
    IPspDefinitionResponse,
    ICreatePspRequest,
    IUpdatePspRequest,
    IPspCurrencyResponse,
    ICreateCurrencyRequest,
    IUpdateCurrencyRequest,
    IPspSettingResponse,
    ICreateSettingRequest,
    IUpdateSettingRequest,
    IPspErrorCodeResponse,
    ICreateErrorCodeRequest,
    IUpdateErrorCodeRequest,
    IPspLoginInfoResponse,
    ICreateLoginInfoRequest,
    IUpdateLoginInfoRequest,
    ITransactionLogResponse
} from '../interfaces/payment-definitions.interface';

@Injectable({
    providedIn: 'root'
})
export class PaymentDefinitionsService {
    private http = inject(HttpClient);
    private baseUrl = environment.apiUrl + '/payment-definitions';

    // Modal Information - Dropdowns
    getTenantTypeInformation(): Observable<ITypeListResponse> {
        return this.http.get<ITypeListResponse>(`${this.baseUrl}/tenant-type-information`);
    }

    getPspTypeInformation(): Observable<ITypeListResponse> {
        return this.http.get<ITypeListResponse>(`${this.baseUrl}/psp-type-information`);
    }

    getServiceProviders(): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/service-provider-information`);
    }

    getPaymentMethods(): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/payment-method-information`);
    }

    getCurrencyCodes(): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/currency-code-information`);
    }

    // Tenant Definition
    getTenants(): Observable<ITenantDefinitionResponse> {
        return this.http.get<ITenantDefinitionResponse>(`${this.baseUrl}/tenants`);
    }

    createTenant(request: ICreateTenantRequest): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/tenants`, request);
    }

    updateTenant(id: number, request: IUpdateTenantRequest): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/tenants/${id}`, request);
    }

    // Tenant User Definition
    getTenantUsers(): Observable<ITenantUserDefinitionResponse> {
        return this.http.get<ITenantUserDefinitionResponse>(`${this.baseUrl}/tenant-users`);
    }

    createTenantUser(request: ICreateTenantUserRequest): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/tenant-users`, request);
    }

    updateTenantUser(id: number, request: IUpdateTenantUserRequest): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/tenants/${id}`, request);
    }

    // PSP Definition
    getPspDefinitions(): Observable<IPspDefinitionResponse> {
        return this.http.get<IPspDefinitionResponse>(`${this.baseUrl}/psp`);
    }

    createPsp(request: ICreatePspRequest): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/psp`, request);
    }

    updatePsp(id: number, request: IUpdatePspRequest): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/psp/${id}`, request);
    }

    // PSP Currency
    getCurrencies(): Observable<IPspCurrencyResponse> {
        return this.http.get<IPspCurrencyResponse>(`${this.baseUrl}/currencies`);
    }

    createCurrency(request: ICreateCurrencyRequest): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/currencies`, request);
    }

    updateCurrency(id: number, request: IUpdateCurrencyRequest): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/currencies/${id}`, request);
    }

    // PSP Setting
    getSettings(): Observable<IPspSettingResponse> {
        return this.http.get<IPspSettingResponse>(`${this.baseUrl}/settings`);
    }

    createSetting(request: ICreateSettingRequest): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/settings`, request);
    }

    updateSetting(id: number, request: IUpdateSettingRequest): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/settings/${id}`, request);
    }

    // PSP Error Code
    getErrorCodes(): Observable<IPspErrorCodeResponse> {
        return this.http.get<IPspErrorCodeResponse>(`${this.baseUrl}/error-codes`);
    }

    createErrorCode(request: ICreateErrorCodeRequest): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/error-codes`, request);
    }

    updateErrorCode(id: number, request: IUpdateErrorCodeRequest): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/error-codes/${id}`, request);
    }

    // Provider Login Info
    getLoginInfo(): Observable<IPspLoginInfoResponse> {
        return this.http.get<IPspLoginInfoResponse>(`${this.baseUrl}/login-info`);
    }

    createLoginInfo(request: ICreateLoginInfoRequest): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/login-info`, request);
    }

    updateLoginInfo(id: number, request: IUpdateLoginInfoRequest): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/login-info/${id}`, request);
    }

    deleteLoginInfo(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/login-info/${id}`);
    }

    // Transaction Log
    getTransactionLogs(startDate: string, endDate: string): Observable<ITransactionLogResponse> {
        const params = new HttpParams().set('startDate', startDate).set('endDate', endDate);
        return this.http.get<ITransactionLogResponse>(`${this.baseUrl}/transaction-logs`, { params });
    }

    completeReservation(id: number): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/complete-reservation/${id}`, null);
    }
}
